import { ContactProvider } from '../../../../Model/Lead';
import { getMediaTypeFromUrl, getMediaData } from '../../../../../utils/attachments';
import { MAX_FILE_UPLOAD_SIZE } from '../../../../../utils/files';
import {
  IMsgIcon,
  EmailIcon,
  InstagramIcon,
  COMMENT_BOX_CLASS_NAME,
} from './styles';
import texts from './texts.json';

/**
 * Maps contact providers to their corresponding icon components.
 * Used to display the appropriate icon in chat message avatars.
 * @type {Object.<string, React.ComponentType>}
 */
const ProviderIcon = {
  [ContactProvider.iMessage]: IMsgIcon,
  [ContactProvider.EMAIL]: EmailIcon,
  [ContactProvider.INSTAGRAM]: InstagramIcon,
  DEFAULT: IMsgIcon,
};

const MessageFilter = {
  TEXT_EMAIL: 'TEXT_EMAIL',
  TEXT_ONLY: 'TEXT_ONLY',
  EMAIL: 'EMAIL',
  INSTAGRAM: 'INSTAGRAM',
};

const MessageFilterConfig = {
  [MessageFilter.TEXT_EMAIL]: { label: texts.textEmail },
  [MessageFilter.TEXT_ONLY]: { label: texts.text },
  [MessageFilter.EMAIL]: { label: texts.email },
  [MessageFilter.INSTAGRAM]: { label: texts.instagram },
};

const SendMode = {
  TEXT: 'TEXT',
  EMAIL: 'EMAIL',
  COMMENT: 'COMMENT',
};

const SendModeConfig = {
  [SendMode.TEXT]: { label: texts.text },
  [SendMode.EMAIL]: { label: texts.email },
  [SendMode.COMMENT]: { label: texts.commentChannel },
};

/**
 * Send channels offered by the composer for each transcript filter.
 *
 * Comments sit alongside whichever outbound channel(s) the filter allows so that leaving
 * one is never harder than sending a message, and never shares a control with one.
 * Instagram is absent deliberately: it has its own send path and no comments.
 */
const SendModesByFilter = {
  [MessageFilter.TEXT_EMAIL]: [SendMode.TEXT, SendMode.EMAIL, SendMode.COMMENT],
  [MessageFilter.TEXT_ONLY]: [SendMode.TEXT, SendMode.COMMENT],
  [MessageFilter.EMAIL]: [SendMode.EMAIL, SendMode.COMMENT],
};

/**
 * Builds the composer's channel options for a transcript filter.
 * @param {string} messageFilter - The active MessageFilter.
 * @returns {{value: string, label: string}[]} Options for the channel toggle, empty when
 * the filter has no composer channels of its own (Instagram).
 */
const getSendModeOptions = (messageFilter) => (SendModesByFilter[messageFilter] || [])
  .map((mode) => ({ value: mode, label: SendModeConfig[mode].label }));

/**
 * Whether a transcript filter offers a given send channel.
 * @param {string} messageFilter - The active MessageFilter.
 * @param {string} sendMode - A SendMode value.
 * @returns {boolean} True when the filter's composer offers that channel.
 */
const isSendModeAvailable = (messageFilter, sendMode) => (
  (SendModesByFilter[messageFilter] || []).includes(sendMode)
);

/**
 * The channel a filter falls back to whenever the transcript filter changes.
 * Always an outbound channel — switching tabs must never silently arm a comment.
 * @param {string} messageFilter - The active MessageFilter.
 * @returns {string} A SendMode value.
 */
const getDefaultSendMode = (messageFilter) => (
  messageFilter === MessageFilter.EMAIL ? SendMode.EMAIL : SendMode.TEXT
);

/**
 * Get the first valid file from the file input event (single file, size check).
 * @param {Event} event - The change event from a file input.
 * @returns {File[]} Array with one file if valid, otherwise empty.
 */
const onFilesSelected = (event) => {
  const { files } = event.target;
  if (files?.length) {
    const file = files[0];
    if (file.size < MAX_FILE_UPLOAD_SIZE) {
      return [file];
    }
  }
  return [];
};

/**
 * Transforms a lead message object into a format compatible with the chat data source component.
 *
 * This function processes a message from the lead messaging system and converts it
 * into the structure expected by the chat UI component. It handles:
 * - Text messages: Returns with type 'text' and message body.
 * - Media attachments: Detects media type (photo/video/audio/file) from the attachment
 *   URL and includes appropriate media data for rendering.
 *
 * @param {LeadMsg} msg - The lead message object to transform.
 * @param {string} name - Display name of the lead.
 * @returns {Object} A data source item formatted for the chat component.
 *
 */
const mapMsgToDataSourceItem = (msg, name) => {
  const base = {
    id: msg.id,
    title: msg.isFromMe ? msg.sentAsUsername : name,
    date: msg.sentAt,
    position: msg.isFromMe ? 'right' : 'left',
    text: msg.body || '',
    ...(msg.isComment ? {
      className: COMMENT_BOX_CLASS_NAME,
    } : {
      avatar: ProviderIcon[msg.provider] || ProviderIcon.DEFAULT,
    }),
  };
  if (msg.attachment) {
    const mediaType = getMediaTypeFromUrl(msg.attachment);
    return {
      ...base,
      type: mediaType,
      data: getMediaData(msg.attachment, mediaType),
    };
  }
  return { ...base, type: 'text' };
};

/**
 * Transforms a lead comment object into a format compatible with the lead message object.
 * @param {object} comment - The lead comment object to transform.
 * @returns {object} A data source item formatted for the lead message object.
 */
const mapCommentToMsg = (comment) => ({
  id: comment.id,
  provider: '',
  sentAt: comment.createdAt?.toDate() || new Date(),
  isFromMe: true,
  sentAsUsername: comment.createdByUserName || '',
  body: comment.value || '',
  isComment: true,
  createdBy: comment.createdBy || '',
});

export {
  ProviderIcon,
  mapMsgToDataSourceItem,
  onFilesSelected,
  MessageFilter,
  MessageFilterConfig,
  SendMode,
  getSendModeOptions,
  isSendModeAvailable,
  getDefaultSendMode,
  mapCommentToMsg,
};
